'use client';

import type { ProductToCompareT } from '../../../../../type/product/productComparison';
import { Button } from '@vs-shop/ui/button';
import {
  isMaxOrderableExceededInCart,
  shouldAddToCartButtonBeDisabled,
} from '../../../../../shared/utils/stockValidation';
import { Array } from 'effect';
import { useIntl } from 'react-intl';

import { showErrorToast } from '@vs-shop/ui/toast';
import { IconCartOutline } from '@vs-shop/ui/icons';

import { useAddToCart, useVariantInCartLookup } from '../../../../../hook/useAddToCart';
import {
  useCartLinePreviewPreload,
  withCartLinePreview,
} from '../../../../../hook/cartLinePreview';

type Props = {
  products: readonly ProductToCompareT[];
};

export default function ProductComparisonFooterAddAllToCart({ products }: Props) {
  const intl = useIntl();

  const addToCart = useAddToCart();
  const variantInCart = useVariantInCartLookup();

  const productsWithQuantities = products.map((product) => ({
    product,
    ...variantInCart(product.code),
  }));
  const isAdding = productsWithQuantities.some((entry) => entry.isAdding);

  // Products already at max stock, or with price 0, cannot be added.
  const productsOrderable = productsWithQuantities.filter(
    ({ product, quantityInCart }) =>
      !shouldAddToCartButtonBeDisabled(
        product.stock.eshop.maxOrderable,
        quantityInCart,
        1,
        product.price
      ) && !isMaxOrderableExceededInCart(product.stock.eshop.maxOrderable, quantityInCart)
  );

  const cartRequests = productsOrderable.map(({ product }) =>
    withCartLinePreview(
      {
        product: {
          code: product.code,
          stock: product.stock,
          name: product.name,
          price: product.price,
          priceWithoutTax: product.priceWithoutTax,
          brand: product.brand,
        },
        quantity: 1,
      },
      product.imagePath,
      product.ageRestriction
    )
  );
  const preloadHandlers = useCartLinePreviewPreload(cartRequests.map((request) => request.preview));

  const handleAddAllToCart = () => {
    if (!Array.isReadonlyArrayNonEmpty(cartRequests)) {
      showErrorToast(
        intl.formatMessage({
          id: 'product.comparison.footer.noProductsToAdd',
          defaultMessage: 'Žádné produkty nelze přidat do košíku.',
        })
      );
      return;
    }

    addToCart(cartRequests);
  };

  return (
    <Button
      variant="primary"
      {...preloadHandlers}
      onClick={handleAddAllToCart}
      loading={isAdding}
      disabled={productsOrderable.length === 0 || isAdding}
    >
      <IconCartOutline className="size-6 shrink-0" />
      <span className="leading-none uppercase">
        {intl.formatMessage({
          id: 'product.comparison.footer.addAllProductsToCart',
          defaultMessage: 'Vložit vše do košíku',
        })}
      </span>
    </Button>
  );
}
